import { createSelector } from 'reselect';
import { getIRALimitData } from '../IRA/IRA.selectors';
import { ApplicationState } from '../../types';
import { getSelectedIRAAutoMaxFunds } from '../../shoppingCart/shoppingCart.selectors';
import cartesianProduct from 'cartesian-product';
import { roundDownToNearestHundredthsPlace } from 'src/utils/utils';
import { getAllMonthsAndDays } from '../../automaticInvestments/automaticInvestments.selectors';

export const contributionMinimum = 100.0;

export const getIRAAutoMaxCartDetailsRaw = ({
  iraAutoMaxCartDetails,
}: ApplicationState) => iraAutoMaxCartDetails;

export const getStartAndEndDateTuple = createSelector(
  [getIRAAutoMaxCartDetailsRaw],
  (iraAutoMaxCartDetails) => [
    iraAutoMaxCartDetails.firstAvailableDate,
    iraAutoMaxCartDetails.lastAvailableDate,
  ]
);

export const getIRASelectedDayCount = createSelector(
  [getIRAAutoMaxCartDetailsRaw],
  (iraInformation) => {
    const { daysOfMonthPayments } = iraInformation;
    return daysOfMonthPayments.length;
  }
);

export const getIRASelectedDaysArray = createSelector(
  [getIRAAutoMaxCartDetailsRaw],
  (iraInformation) => {
    const { daysOfMonthPayments } = iraInformation;
    return daysOfMonthPayments.map((selectedDays) => selectedDays.value);
  }
);

export const getIRASelectedMonthsArray = createSelector(
  [getIRAAutoMaxCartDetailsRaw],
  (iraInformation) => {
    const { monthsForPayments } = iraInformation;
    return monthsForPayments.map((selectedMonths) => selectedMonths.value);
  }
);

export const didUserSelectSameDays = createSelector(
  [getAllMonthsAndDays, getIRASelectedDaysArray],
  (previousDays, currentDays) => {
    previousDays?.selectedDays?.sort(function (a, b) {
      return a - b;
    });
    currentDays.sort(function (a, b) {
      return a - b;
    });
    return (
      Array.isArray(previousDays?.selectedDays) &&
      Array.isArray(currentDays) &&
      previousDays?.selectedDays.length === currentDays.length &&
      previousDays?.selectedDays.every(
        (val, index) => val === currentDays[index]
      )
    );
  }
);

export const didUserSelectSameMonths = createSelector(
  [getAllMonthsAndDays, getIRASelectedMonthsArray],
  (previousMonths, currentMonths) => {
    previousMonths?.selectedMonths?.sort(function (a, b) {
      return a - b;
    });
    currentMonths.sort(function (a, b) {
      return a - b;
    });
    return (
      Array.isArray(previousMonths?.selectedMonths) &&
      Array.isArray(currentMonths) &&
      previousMonths?.selectedMonths.length === currentMonths.length &&
      previousMonths?.selectedMonths.every(
        (val, index) => val === currentMonths[index]
      )
    );
  }
);

export const getMonthsDaysCartesianProduct = createSelector(
  [getIRAAutoMaxCartDetailsRaw],
  ({ monthsForPayments, daysOfMonthPayments }) =>
    cartesianProduct([
      monthsForPayments.map((_) => _.value).sort((a, b) => a - b),
      daysOfMonthPayments.map((_) => _.value).sort((a, b) => a - b),
    ])
);

export const getRemainingContributionDatesSorted = createSelector(
  [getMonthsDaysCartesianProduct, getStartAndEndDateTuple],
  (monthsDaysCartesianProduct, [startDate, endDate]) => {
    // set to start of day according to local time

    if (!startDate || !endDate) {
      return [];
    }
    const currYear = new Date().getFullYear();
    const startDateMonth = startDate.getMonth();
    const startDateDay = startDate.getDate();
    const startDateYear = startDate.getFullYear();
    const endDateYear = endDate.getFullYear();
    const endDateMonth = endDate.getMonth();
    const endDateDay = endDate.getDate();
    return monthsDaysCartesianProduct.filter(([m, d]) => {
      // get user selected month
      // get user selected day
      // keep date where within startDate (inclusive) and endDate (inclusive)

      //if start and end month are the same as well as the same month the user selected if the year is right and the days
      //are between start and end date
      if (
        m <= endDateMonth &&
        m >= startDateMonth &&
        currYear >= startDateYear &&
        currYear <= endDateYear
      ) {
        if (
          startDateMonth === endDateMonth &&
          (d < startDateDay || d > endDateDay)
        ) {
          return false;
        } else if (d < startDateDay && m === startDateMonth) {
          return false;
        } else if (d > endDateDay && m === endDateMonth) {
          return false;
        }
        return true;
      }
      // else you return false
      return false;
    });
  }
);

export const getActualCurrentYearRemainingContributions = createSelector(
  [getIRALimitData],
  ({ currentYearContributionsSoFar, currentYearMaximumLimit }) =>
    currentYearMaximumLimit - currentYearContributionsSoFar
);

export const getRemainingContributionDatesCount = createSelector(
  [getRemainingContributionDatesSorted],
  (remainingContributionDates) => remainingContributionDates.length
);

/**
 * Gets the ira-auto-max contribution per account
 *
 *
 */
export const getContributionPerAccount = createSelector(
  [
    getRemainingContributionDatesCount,
    getActualCurrentYearRemainingContributions,
    getSelectedIRAAutoMaxFunds,
  ],
  (
    remainingContributionCount,
    actualCurrentYearRemainingContributions,
    selectedIRAAutoMaxFunds
  ) => {
    if (!Array.isArray(selectedIRAAutoMaxFunds)) {
      throw new TypeError(
        'Expected selectedIRAAutoMaxFunds to be of type Array'
      );
    }
    const product = remainingContributionCount * selectedIRAAutoMaxFunds.length;
    return (
      product &&
      roundDownToNearestHundredthsPlace(
        actualCurrentYearRemainingContributions / product
      )
    );
  }
);

export const getContributionPerAccountMeetsMinimum = createSelector(
  [getContributionPerAccount],
  (contributionPerAccount) => contributionPerAccount >= contributionMinimum
);
